import React, { useState, useMemo } from "react";
import {
  Table,
  Input,
  Select,
  Space,
  Tag,
  Typography,
  Spin,
  Card,
} from "antd";
import { Search, BarChart3, Users, Clock } from "lucide-react";
import type { ColumnsType, TableProps } from "antd/es/table";
import { useGetServiceTickets } from "~/services/service-ticket/service-ticket.queries";
import { TServiceTicket } from "~/types/tickets";
import { ServiceType, JobStatus, SortOrder } from "~/utils/constants";
import { calculateCubicFeetInventory, capitalize, formatDate } from "~/utils";
import { getTravelTimeHours, getDurationHours } from "~/utils/service-cost-calculator";
import dayjs from "dayjs";

const { Text } = Typography;

interface ServiceKPIRow {
  id: string;
  serial: string;
  serviceDate: string;
  serviceType: ServiceType;
  status: JobStatus;
  duration: number | null;
  staffCount: number;
  inventoryCount: number;
  inventoryVolume: number;
  travelTime: number | null;
  clientName: string;
  location: string;
}

export const ServiceKPIsTab: React.FC = () => {
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<JobStatus | "all">("all");
  const [serviceTypeFilter, setServiceTypeFilter] = useState<ServiceType | "all">("all");

  const { data: serviceTicketsData, isLoading } = useGetServiceTickets(
    {
      page: 1,
      pageSize: 1000, // Get a large dataset for filtering
      sortByCreatedAt: SortOrder.DESC,
    },
    true
  );

  const serviceTickets = serviceTicketsData?.items || [];

  // Transform service tickets into KPI rows
  const kpiRows: ServiceKPIRow[] = useMemo(() => {
    return serviceTickets.map((ticket: TServiceTicket) => {
      const inventoryVolume = ticket.inventories
        ? calculateCubicFeetInventory(ticket.inventories).totalCubicFeet
        : 0;
      const numericVolume =
        typeof inventoryVolume === "string"
          ? parseFloat(inventoryVolume)
          : inventoryVolume || 0;

      return {
        id: ticket.id,
        serial: ticket.serial || "-",
        serviceDate: ticket.serviceDatetime
          ? new Date(ticket.serviceDatetime).toISOString()
          : ticket.startDatetime
          ? new Date(ticket.startDatetime).toISOString()
          : "",
        serviceType: ticket.serviceType,
        status: ticket.status,
        duration: ticket.duration || null,
        staffCount: ticket.staffs?.length || 0,
        inventoryCount: ticket.inventories?.length || 0,
        inventoryVolume: numericVolume,
        travelTime: ticket.travelTime || null,
        clientName:
          ticket.client?.companyName ||
          `${ticket.client?.firstName || ""} ${ticket.client?.lastName || ""}`.trim() ||
          "N/A",
        location: ticket.location
          ? `${ticket.location.address || ""}, ${ticket.location.city || ""} ${ticket.location.state || ""}`.trim()
          : "N/A",
      };
    });
  }, [serviceTickets]);

  // Apply filters
  const filteredRows = useMemo(() => {
    let filtered = kpiRows;

    // Search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(
        (row) =>
          row.serial.toLowerCase().includes(searchLower) ||
          row.clientName.toLowerCase().includes(searchLower) ||
          row.location.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((row) => row.status === statusFilter);
    }

    // Service type filter
    if (serviceTypeFilter !== "all") {
      filtered = filtered.filter((row) => row.serviceType === serviceTypeFilter);
    }

    return filtered;
  }, [kpiRows, searchText, statusFilter, serviceTypeFilter]);

  const getStatusColor = (status: JobStatus) => {
    const colors: Record<JobStatus, string> = {
      [JobStatus.DRAFT]: "default",
      [JobStatus.SCHEDULED]: "blue",
      [JobStatus.IN_PROGRESS]: "processing",
      [JobStatus.COMPLETED]: "success",
      [JobStatus.CANCELLED]: "error",
      [JobStatus.ON_HOLD]: "warning",
    };
    return colors[status] || "default";
  };

  const getServiceTypeColor = (type: ServiceType) => {
    const colors: Record<ServiceType, string> = {
      [ServiceType.COLLECTION]: "blue",
      [ServiceType.DELIVERY]: "green",
      [ServiceType.ON_SITE_SERVICE]: "orange",
      [ServiceType.WAREHOUSE_SERVICE]: "purple",
      [ServiceType.FABRICATION_SERVICE]: "cyan",
      [ServiceType.PACKING]: "magenta",
    };
    return colors[type] || "default";
  };

  const columns: ColumnsType<ServiceKPIRow> = [
    {
      title: "Service ID",
      dataIndex: "serial",
      key: "serial",
      width: 120,
      sorter: (a, b) => (a.serial || "").localeCompare(b.serial || ""),
      render: (serial) => (
        <Text className="font-mono text-sm">{serial}</Text>
      ),
    },
    {
      title: "Date",
      dataIndex: "serviceDate",
      key: "serviceDate",
      width: 120,
      sorter: (a, b) => {
        const dateA = a.serviceDate ? new Date(a.serviceDate).getTime() : 0;
        const dateB = b.serviceDate ? new Date(b.serviceDate).getTime() : 0;
        return dateA - dateB;
      },
      render: (date) => (date ? formatDate(new Date(date)) : "-"),
    },
    {
      title: "Service Type",
      dataIndex: "serviceType",
      key: "serviceType",
      width: 150,
      sorter: (a, b) => a.serviceType.localeCompare(b.serviceType),
      render: (type: ServiceType) => (
        <Tag color={getServiceTypeColor(type)}>
          {capitalize(type.replace("_", " "))}
        </Tag>
      ),
      filters: Object.values(ServiceType).map((type) => ({
        text: capitalize(type.replace("_", " ")),
        value: type,
      })),
      onFilter: (value, record) => record.serviceType === value,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (status: JobStatus) => (
        <Tag color={getStatusColor(status)}>
          {capitalize(status.replace("_", " "))}
        </Tag>
      ),
      filters: Object.values(JobStatus).map((status) => ({
        text: capitalize(status.replace("_", " ")),
        value: status,
      })),
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Duration (hrs)",
      dataIndex: "duration",
      key: "duration",
      width: 120,
      align: "right",
      sorter: (a, b) => {
        const durA = a.duration || 0;
        const durB = b.duration || 0;
        return durA - durB;
      },
      render: (duration) =>
        duration !== null ? `${duration.toFixed(2)}` : "-",
    },
    {
      title: "Staff",
      dataIndex: "staffCount",
      key: "staffCount",
      width: 80,
      align: "center",
      sorter: (a, b) => a.staffCount - b.staffCount,
      render: (count) => <Text strong={count > 0}>{count}</Text>,
    },
    {
      title: "Items",
      dataIndex: "inventoryCount",
      key: "inventoryCount",
      width: 80,
      align: "center",
      sorter: (a, b) => a.inventoryCount - b.inventoryCount,
      render: (count) => <Text>{count}</Text>,
    },
    {
      title: "Volume (ft³)",
      dataIndex: "inventoryVolume",
      key: "inventoryVolume",
      width: 120,
      align: "right",
      sorter: (a, b) => a.inventoryVolume - b.inventoryVolume,
      render: (volume) => (
        <Text>{volume > 0 ? volume.toFixed(2) : "-"}</Text>
      ),
    },
    {
      title: "Travel Time (hrs)",
      dataIndex: "travelTime",
      key: "travelTime",
      width: 130,
      align: "right",
      sorter: (a, b) => {
        const timeA = a.travelTime || 0;
        const timeB = b.travelTime || 0;
        return timeA - timeB;
      },
      render: (time) => (time !== null ? `${time.toFixed(2)}` : "-"),
    },
    {
      title: "Client",
      dataIndex: "clientName",
      key: "clientName",
      width: 200,
      sorter: (a, b) => a.clientName.localeCompare(b.clientName),
      render: (name) => <Text ellipsis>{name}</Text>,
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 250,
      render: (location) => <Text ellipsis className="text-gray-600">{location}</Text>,
    },
  ];

  const handleTableChange: TableProps<ServiceKPIRow>["onChange"] = () => {
    // Sorting is handled by Ant Design Table's built-in sorter
    // No need for custom state management
  };

  // Get today's services for staff booking metrics
  const todaysServices = useMemo(() => {
    const today = dayjs().startOf('day');
    return serviceTickets.filter((ticket: TServiceTicket) => {
      const serviceDate = ticket.serviceDatetime 
        ? dayjs(ticket.serviceDatetime)
        : ticket.startDatetime 
        ? dayjs(ticket.startDatetime)
        : null;
      
      if (!serviceDate) return false;
      return serviceDate.isSame(today, 'day');
    });
  }, [serviceTickets]);

  // Calculate staff booking metrics for today
  const todaysStaffMetrics = useMemo(() => {
    const uniqueStaffIds = new Set<string>();
    let totalStaffHours = 0;

    todaysServices.forEach((ticket: TServiceTicket) => {
      // Get travel time (from shipping address/location)
      const travelHours = getTravelTimeHours(ticket);
      
      // Get duration (site time)
      const durationHours = getDurationHours(ticket);
      
      // Total hours = travel + duration
      const totalHours = travelHours + durationHours;
      
      // Apply minimum 4 hours
      const billableHours = Math.max(4, totalHours);
      
      // Staff count
      const staffCount = ticket.staffs?.length || 0;
      
      // Track unique staff
      ticket.staffs?.forEach(staff => {
        if (staff.id) uniqueStaffIds.add(staff.id);
      });
      
      // Add to total staff hours (billable hours × staff count)
      totalStaffHours += billableHours * staffCount;
    });

    return {
      staffBooked: uniqueStaffIds.size,
      totalStaffHours: totalStaffHours,
    };
  }, [todaysServices]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const stats = {
      totalServices: filteredRows.length,
      totalDuration: filteredRows.reduce((sum, row) => sum + (row.duration || 0), 0),
      totalStaffHours: filteredRows.reduce(
        (sum, row) => sum + (row.duration || 0) * row.staffCount,
        0
      ),
      totalVolume: filteredRows.reduce((sum, row) => sum + row.inventoryVolume, 0),
      avgDuration: 0,
      avgStaffCount: 0,
    };

    stats.avgDuration =
      stats.totalServices > 0 ? stats.totalDuration / stats.totalServices : 0;
    stats.avgStaffCount =
      stats.totalServices > 0
        ? filteredRows.reduce((sum, row) => sum + row.staffCount, 0) /
          stats.totalServices
        : 0;

    return stats;
  }, [filteredRows]);

  return (
    <div className="space-y-6 p-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="border-gray-200">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
            Total Services
          </div>
          <div className="text-2xl font-semibold text-gray-900">
            {summaryStats.totalServices}
          </div>
        </Card>
        <Card className="border-gray-200">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
            Avg Duration
          </div>
          <div className="text-2xl font-semibold text-gray-900">
            {summaryStats.avgDuration.toFixed(2)} hrs
          </div>
        </Card>
        <Card className="border-gray-200">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
            Total Volume
          </div>
          <div className="text-2xl font-semibold text-gray-900">
            {summaryStats.totalVolume.toFixed(1)} ft³
          </div>
        </Card>
        <Card className="border-gray-200">
          <div className="flex items-center gap-2 mb-1">
            <Users size={14} className="text-gray-400" />
            <div className="text-xs text-gray-500 uppercase tracking-wide">
              Staff Booked Today
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-900">
            {todaysStaffMetrics.staffBooked}
          </div>
        </Card>
        <Card className="border-gray-200">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={14} className="text-gray-400" />
            <div className="text-xs text-gray-500 uppercase tracking-wide">
              Total Staff Hours (Today)
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-900">
            {todaysStaffMetrics.totalStaffHours.toFixed(1)} hrs
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Min 4 hrs/service
          </div>
        </Card>
        <Card className="border-gray-200">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
            Services Today
          </div>
          <div className="text-2xl font-semibold text-gray-900">
            {todaysServices.length}
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-gray-200" bodyStyle={{ padding: "16px" }}>
        <Space direction="vertical" className="w-full" size="middle">
          <Space wrap className="w-full">
            <Input
              placeholder="Search by service ID, client, or location..."
              prefix={<Search size={16} className="text-gray-400" />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
              allowClear
            />
            <Select
              placeholder="Filter by Status"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 150 }}
            >
              <Select.Option value="all">All Statuses</Select.Option>
              {Object.values(JobStatus).map((status) => (
                <Select.Option key={status} value={status}>
                  {capitalize(status.replace("_", " "))}
                </Select.Option>
              ))}
            </Select>
            <Select
              placeholder="Filter by Service Type"
              value={serviceTypeFilter}
              onChange={setServiceTypeFilter}
              style={{ width: 180 }}
            >
              <Select.Option value="all">All Types</Select.Option>
              {Object.values(ServiceType).map((type) => (
                <Select.Option key={type} value={type}>
                  {capitalize(type.replace("_", " "))}
                </Select.Option>
              ))}
            </Select>
          </Space>
        </Space>
      </Card>

      {/* Table */}
      <Card
        className="border-gray-200"
        title={
          <div className="flex items-center gap-2">
            <BarChart3 size={18} className="text-gray-600" />
            <span className="text-gray-900 font-semibold">Service KPIs</span>
            <Text type="secondary" className="text-sm font-normal">
              ({filteredRows.length} services)
            </Text>
          </div>
        }
      >
        <Table
          columns={columns}
          dataSource={filteredRows}
          rowKey="id"
          loading={isLoading}
          onChange={handleTableChange}
          scroll={{ x: 1400 }}
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} services`,
            pageSizeOptions: ["25", "50", "100", "200"],
          }}
        />
      </Card>
    </div>
  );
};

